// Card — neutral surface container (border + radius + subtle shadow).
// `padded` adds the standard inner padding; turn it off to nest full-bleed
// content (tables, list rows) that owns its own spacing.
// `head` renders a bordered header section above the body (each self-padded).

import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Apply the standard inner padding. Default true. */
  padded?: boolean;
  /** Optional header section rendered above the body, split by a divider. */
  head?: ReactNode;
}

const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { padded = true, head, className = '', children, ...rest },
  ref,
) {
  const hasHead = head != null;
  // With a head the section + body pad themselves (so the divider is full-bleed);
  // without one, the root keeps the simple `--pad` behavior.
  const classes = ['app-card', padded && !hasHead && 'app-card--pad', className]
    .filter(Boolean)
    .join(' ');

  return (
    <div ref={ref} className={classes} {...rest}>
      {hasHead && <div className='app-card__head'>{head}</div>}
      {hasHead ? (
        <div
          className={['app-card__body', padded && 'app-card__body--pad'].filter(Boolean).join(' ')}
        >
          {children}
        </div>
      ) : (
        children
      )}
    </div>
  );
});

export default Card;
